import { useCallback, useEffect, useRef, useState } from 'react';
import { Types } from '@cornerstonejs/core';

import { useVideoAnimationLoop } from './useVideoAnimationLoop';

interface UseVideoPlaybackProps {
  viewport: unknown | null; // IStackViewport | IVideoViewport
  isVideoViewport: boolean;
  viewportId: string;
  frameCount: number;
  cineSpeedMultiplier: number;
  subscribeToVideoPause: (cb: () => void) => () => void;
  subscribeToVideoToggle: (cb: () => void) => () => void;
  deactivateTool: () => void;
}

interface UseVideoPlaybackReturn {
  currentFrame: number;
  isPlaying: boolean;
  handleFrameChange: (frameIndex: number) => void;
  pause: () => void;
  togglePlayPause: () => void;
  handlePreviousFrame: () => void;
  handleNextFrame: () => void;
  /** Ref for wheel handler to read current frame without closure deps */
  currentFrameRef: React.MutableRefObject<number>;
  /** Ref for wheel handler to read playing state without closure deps */
  isPlayingRef: React.MutableRefObject<boolean>;
  /** Ref to prevent animation loop from overwriting state during seek */
  seekingRef: React.MutableRefObject<boolean>;
}

export function useVideoPlayback({
  viewport,
  isVideoViewport,
  viewportId,
  frameCount,
  cineSpeedMultiplier,
  subscribeToVideoPause,
  subscribeToVideoToggle,
  deactivateTool,
}: UseVideoPlaybackProps): UseVideoPlaybackReturn {
  // Local state for video viewport playback
  const [videoCurrentFrame, setVideoCurrentFrame] = useState(0);
  const [videoIsPlaying, setVideoIsPlaying] = useState(false);

  // Refs for wheel event handler - avoids recreating listener on every frame change
  // This prevents 960 listener add/remove operations per second with 16 viewports at 60 FPS
  const currentFrameRef = useRef(videoCurrentFrame);
  const isPlayingRef = useRef(videoIsPlaying);

  // Track when user is actively seeking to prevent animation loop from overwriting state
  // This prevents the slider from "jumping back" during seek operations
  const seekingRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    currentFrameRef.current = videoCurrentFrame;
    isPlayingRef.current = videoIsPlaying;
  }, [videoCurrentFrame, videoIsPlaying]);

  // Subscribe to video pause signal for measurement tool activation
  // When measurement tools (Length, Angle, Freehand ROI) are activated,
  // all videos must be paused for accurate measurements
  useEffect(() => {
    if (!isVideoViewport || !viewport) return () => {};

    const videoViewport = viewport as Types.IVideoViewport;

    const unsubscribe = subscribeToVideoPause(() => {
      videoViewport.pause();
    });

    return unsubscribe;
  }, [isVideoViewport, viewport, subscribeToVideoPause]);

  // Subscribe to global toggle signal for spacebar hotkey
  // When spacebar is pressed on any viewport, all video viewports toggle
  useEffect(() => {
    if (!isVideoViewport || !viewport) return () => {};

    const videoViewport = viewport as Types.IVideoViewport;

    const unsubscribe = subscribeToVideoToggle(() => {
      // Access internal video element to check paused state
      const videoElement =
        (videoViewport as unknown as { videoElement?: HTMLVideoElement })
          .videoElement ||
        (videoViewport as unknown as { video?: HTMLVideoElement }).video;

      const isPaused = videoElement?.paused ?? true;

      if (isPaused) {
        videoViewport.play().catch(() => {
          // Handle error gracefully
        });
      } else {
        videoViewport.pause();
      }
    });

    return unsubscribe;
  }, [isVideoViewport, viewport, subscribeToVideoToggle, viewportId]);

  // Cornerstone derives the frame as 1 + round(time × fps), so the last
  // half-frame of a clip reports N+1 — one more value than the clip has frames.
  // Clamp so the app only ever sees the clip's N frames.
  const setClampedFrame = useCallback(
    (frame: number) =>
      setVideoCurrentFrame(Math.max(0, Math.min(frame, frameCount - 1))),
    [frameCount]
  );

  // Track playback state + drive Cornerstone's video render loop via RAF.
  useVideoAnimationLoop({
    viewport,
    isVideoViewport,
    seekingRef,
    setCurrentFrame: setClampedFrame,
    setIsPlaying: setVideoIsPlaying,
  });

  // Apply this pane's cine speed to its video element. Each pane owns its own
  // multiplier, so the cine FPS control only affects the selected viewport —
  // re-running here only when *this* pane's multiplier changes.
  useEffect(() => {
    if (!isVideoViewport || !viewport) return;

    const videoViewport = viewport as Types.IVideoViewport;
    const { videoElement } = videoViewport as unknown as {
      videoElement?: HTMLVideoElement;
    };

    if (!videoElement) return;

    // playbackRate is applied directly as a multiplier against the video's natural speed.
    // Clamp to the browser-safe range (Chrome/Edge: 0.0625–16, Safari: 0.5–2).
    videoElement.playbackRate = Math.max(
      0.0625,
      Math.min(cineSpeedMultiplier, 16)
    );
  }, [isVideoViewport, viewport, cineSpeedMultiplier, viewportId]);

  // Callbacks

  const handleFrameChange = useCallback(
    (frameIndex: number) => {
      if (!isVideoViewport || !viewport) return;

      // Set seeking flag to prevent animation loop from overwriting state
      seekingRef.current = true;

      // Optimistically update React state for responsive slider
      setVideoCurrentFrame(frameIndex);

      const videoViewport = viewport as Types.IVideoViewport;
      // Convert 0-based app frame index to 1-based for Cornerstone
      videoViewport
        .setFrameNumber(frameIndex + 1)
        .then(() => {
          // Clear seeking flag after seek completes
          seekingRef.current = false;
        })
        .catch(() => {
          // Seek failed - clear seeking flag to restore normal operation
          seekingRef.current = false;
        });
    },
    [isVideoViewport, viewport]
  );

  const pause = useCallback(() => {
    if (!isVideoViewport || !viewport) return;

    const videoViewport = viewport as Types.IVideoViewport;
    videoViewport.pause();
  }, [isVideoViewport, viewport]);

  const handleNextFrame = useCallback(() => {
    if (videoIsPlaying) pause();
    handleFrameChange(Math.min(videoCurrentFrame + 1, frameCount - 1));
  }, [handleFrameChange, videoCurrentFrame, frameCount, videoIsPlaying, pause]);

  const handlePreviousFrame = useCallback(() => {
    if (videoIsPlaying) pause();
    handleFrameChange(Math.max(videoCurrentFrame - 1, 0));
  }, [handleFrameChange, videoCurrentFrame, videoIsPlaying, pause]);

  // Toggle play/pause for video viewports
  const togglePlayPause = useCallback(() => {
    if (!isVideoViewport || !viewport) return;

    const videoViewport = viewport as Types.IVideoViewport;

    // Access internal video element to check paused state
    const videoElement =
      (videoViewport as unknown as { videoElement?: HTMLVideoElement })
        .videoElement ||
      (videoViewport as unknown as { video?: HTMLVideoElement }).video;

    const isPaused = videoElement?.paused ?? !videoIsPlaying;

    // Use viewport.play()/pause() NOT videoElement methods
    // viewport methods manage the rendering loop that draws frames to canvas
    if (isPaused) {
      // Deactivate any active tool when video starts playing
      deactivateTool();
      videoViewport.play().catch((err) => {
        throw new Error(
          `[ViewerPane ${viewportId}] Failed to play video: ${err}`
        );
      });
    } else {
      videoViewport.pause();
    }
  }, [isVideoViewport, viewport, videoIsPlaying, viewportId, deactivateTool]);

  return {
    currentFrame: videoCurrentFrame,
    isPlaying: videoIsPlaying,
    handleFrameChange,
    pause,
    togglePlayPause,
    handlePreviousFrame,
    handleNextFrame,
    currentFrameRef,
    isPlayingRef,
    seekingRef,
  };
}
